import { Button } from '@mantine/core';
import DebtorBatchActions from 'app/Private/Clients/Listing/BatchActions/DebtorBatchActions';
import { ExportDebtorCsv } from 'app/Private/Clients/Listing/ExportDebtorCsv';
import { tKeys, useTranslation } from 'locales';
import {
  type LoadUnhandledDebtorsViewParams,
  useLoadUnhandledDebtorsView,
} from 'modules/AtRisk/api/use-load-unhandled-debtors-view';
import { usePrefetchNextDebtorAtRisk } from 'modules/AtRisk/api/use-prefetch-next-at-risk-debtor';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { queryParamParser } from 'shared/utils/view';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Debtor } from 'types/store/client-state';

const paramsFromLocation = (hash: string, search: string): LoadUnhandledDebtorsViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export const AtRiskIndex = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  usePrefetchNextDebtorAtRisk();

  const [params, setParams] = useState<LoadUnhandledDebtorsViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { unhandledDebtors, isUnhandledDebtorsInitialLoading, refetchUnhandledDebtors } =
    useLoadUnhandledDebtorsView(params);

  const [selectedDebtors, setSelectedDebtors] = useState<Debtor[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const exportDebtors = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(tKeys.EXPORT_CSV),
      children: <ExportDebtorCsv />,
    });
  };

  const openBatchActionsModal = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(tKeys.FORM.ACTIONS),
      children: (
        <DebtorBatchActions
          debtorsSelected={selectedDebtors}
          isSelectedAll={areAllItemsAcrossPagesSelected}
          totalObjects={unhandledDebtors?.metadata?.pagination.total_objects ?? 0}
          reload={refetchUnhandledDebtors}
        />
      ),
    });
  };

  const showDebtor = (item: { id: number }, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(`/clients/${item.id}`, '_blank');
      return;
    }

    history.push(`/clients/${item.id}#at-risk`);
  };

  return (
    <CustomTableViews
      title={t(tKeys.MODULES.AT_RISK.INDEX_TITLE)}
      tableName="unhandled_debtors"
      onClickRow={showDebtor}
      actions={
        selectedDebtors.length > 0 ? (
          <Button onClick={openBatchActionsModal}>{t(tKeys.FORM.ACTIONS)}</Button>
        ) : (
          <Button onClick={exportDebtors}>{t(tKeys.EXPORT)}</Button>
        )
      }
      callbackAction={refetchUnhandledDebtors}
      isLoading={isUnhandledDebtorsInitialLoading}
      pagination={unhandledDebtors?.metadata?.pagination}
      items={unhandledDebtors?.data ?? []}
      onChangeSelectionCallback={values => setSelectedDebtors(values as Array<Debtor>)}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
    />
  );
};
